<template>
  <transition name="fade" appear>
    <div v-if="visible" class="toast-container" :class="variantClass">
      <div class="toast-message">
        {{ message }}
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';

const props = withDefaults(
  defineProps<{
    message: string;
    duration?: number | null;
    variant?: 'success' | 'error' | 'warning' | 'info';
  }>(),
  {
    duration: 3000,
    variant: undefined,
  },
);

// Computed variant class
const variantClass = computed(() => {
  if (!props.variant) return '';
  return `toast-${props.variant}`;
});

const visible = ref(true);

onMounted(() => {
  // Only set timeout if duration is provided (not null for persistent toasts)
  if (props.duration !== null) {
    setTimeout(() => {
      visible.value = false;
    }, props.duration || 3000);
  }
});
</script>

<style scoped>
.toast-container {
  position: relative;
}

.toast-message {
  background-color: #ffffff;
  color: #000000;
  padding: 12px 24px;
  border-radius: 42px;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.43;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  width: max-content;
}

/* Variant styles */
.toast-success .toast-message {
  background-color: #10b981;
  color: #ffffff;
}

.toast-error .toast-message {
  background-color: #ef4444;
  color: #ffffff;
}

.toast-warning .toast-message {
  background-color: #f59e0b;
  color: #ffffff;
}

.toast-info .toast-message {
  background-color: #3b82f6;
  color: #ffffff;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
